// =============================================================================
// src/features/analytics/analytics-chart-markup.ts - Grafico interativo Analytics.
// =============================================================================

import { escapeHtml } from '../../app/dom';
import {
  type OmarAnalyticsChartGranularity,
  type OmarAnalyticsChartMode,
  type OmarAnalyticsChartSample,
  type OmarAnalyticsChartSeries,
} from './analytics-data';
import { analyticsLiveSource, getAnalyticsChartSeries } from './analytics-live-store';

const OMAR_CHART_WIDTH = 720;
const OMAR_CHART_HEIGHT = 250;
const OMAR_CHART_TOP = 28;
const OMAR_CHART_BOTTOM = 196;
const OMAR_CHART_LEFT = 46;
const OMAR_CHART_RIGHT = 18;

/** Renderiza a area funcional do grafico com amostras clicaveis. */
export function buildAnalyticsChartStageMarkup(
  granularity: OmarAnalyticsChartGranularity,
  chartMode: OmarAnalyticsChartMode,
): string {
  const chartSeries = analyticsChartSeriesFor(granularity);
  const activeIndex = chartSeries.points.length - 1;
  const activeSample = analyticsChartSampleFor(chartSeries, activeIndex);
  // Live (2026-07-25): a view de sentimento (serie mock) nem entra no DOM — a
  // tab que alternava para ela tambem some (analyticsChartModeTabsMarkup).
  const sentimentView = analyticsLiveSource() === 'live' ? '' : analyticsChartViewMarkup(chartSeries, 'sentiment', chartMode);
  return `<div class="u-analytics-chart-stage" data-analytics-chart-stage data-analytics-chart-granularity="${granularity}">
    <div class="u-analytics-chart-board">${analyticsChartViewMarkup(chartSeries, 'reach', chartMode)}${sentimentView}</div>
    ${buildAnalyticsChartInspectorMarkup(activeSample, chartMode)}
  </div>`;
}

/** Renderiza o painel lateral do ponto selecionado. */
export function buildAnalyticsChartInspectorMarkup(
  chartSample: OmarAnalyticsChartSample,
  chartMode: OmarAnalyticsChartMode,
): string {
  const metricLine = chartMode === 'reach' ? analyticsReachMetricLine(chartSample) : analyticsSentimentMetricLine(chartSample);
  return `<aside class="u-analytics-chart-inspector" data-analytics-chart-inspector>
    <span data-analytics-inspector-date>${escapeHtml(chartSample.fullLabel)}</span>
    <strong data-analytics-inspector-title>${escapeHtml(chartSample.sampleTitle)}</strong>
    <dl>${metricLine}<div><dt>Fonte dominante</dt><dd>${escapeHtml(chartSample.sourceLabel)}</dd></div></dl>
    <p data-analytics-inspector-text>${escapeHtml(chartSample.sampleText)}</p>
    <small data-analytics-inspector-action>${escapeHtml(chartSample.actionHint)}</small>
  </aside>`;
}

/** Busca uma serie valida para controles de granularidade (getter cobre o
 * render inicial E o re-render runtime do chart-controller — live de graca). */
export function analyticsChartSeriesFor(granularity: OmarAnalyticsChartGranularity): OmarAnalyticsChartSeries {
  const allSeries = getAnalyticsChartSeries();
  const fallbackSeries = allSeries[0];
  if (!fallbackSeries) throw new Error(`analytics chart series missing: received ${JSON.stringify(granularity)}, expected seeded series`);
  return allSeries.find((chartSeries) => chartSeries.granularity === granularity) ?? fallbackSeries;
}

/** Resolve uma amostra valida ou falha com contexto de dados. */
export function analyticsChartSampleFor(
  chartSeries: OmarAnalyticsChartSeries,
  sampleIndex: number,
): OmarAnalyticsChartSample {
  const chartSample = chartSeries.points[sampleIndex] ?? chartSeries.points[chartSeries.points.length - 1];
  if (!chartSample) throw new Error(`analytics chart sample missing: received ${sampleIndex}, expected non-empty chart series`);
  return chartSample;
}

function analyticsChartViewMarkup(
  chartSeries: OmarAnalyticsChartSeries,
  chartMode: OmarAnalyticsChartMode,
  activeMode: OmarAnalyticsChartMode,
): string {
  const isHidden = chartMode === activeMode ? '' : ' hidden';
  return `<div class="u-analytics-chart-view" data-analytics-chart-view="${chartMode}"${isHidden}>
    ${analyticsChartSvgMarkup(chartSeries, chartMode)}${analyticsChartAxisTextMarkup(chartSeries, chartMode)}${analyticsChartSampleLayerMarkup(chartSeries, chartMode)}
  </div>`;
}

// preserveAspectRatio="none" e OBRIGATORIO: o viewBox 720x250 renderiza em um box
// responsivo de outra proporcao, e o default (xMidYMid meet) encolhia o desenho
// para uma faixa letterboxed — a linha ficava esmagada e os botoes-ponto (camada
// HTML posicionada em %) caiam FORA da linha ("grafico nao condiz com os numeros",
// pedido 2026-07-25). Com o stretch, % da camada HTML = coordenada/viewBox exata;
// os strokes nao distorcem (vector-effect="non-scaling-stroke") e TODO texto vive
// em camadas HTML (analyticsChartAxisTextMarkup), nunca em <text> do SVG.
function analyticsChartSvgMarkup(chartSeries: OmarAnalyticsChartSeries, chartMode: OmarAnalyticsChartMode): string {
  const chartMax = analyticsChartMax(chartSeries, chartMode);
  return `<svg class="u-analytics-chart" data-analytics-svg-view="${chartMode}" viewBox="0 0 ${OMAR_CHART_WIDTH} ${OMAR_CHART_HEIGHT}" preserveAspectRatio="none" role="img" aria-label="${analyticsChartAriaLabel(chartMode)}">
    ${analyticsChartGridMarkup(chartMax)}${analyticsChartSeriesMarkup(chartSeries, chartMode, chartMax)}
  </svg>`;
}

/** Valores do grid (esquerda) + rotulos do eixo X em HTML — texto nao pode morar
 * no SVG esticado (escala nao-uniforme deformaria os glifos). Mesma matematica
 * de % dos botoes-ponto, entao alinham com o desenho em qualquer largura. */
function analyticsChartAxisTextMarkup(chartSeries: OmarAnalyticsChartSeries, chartMode: OmarAnalyticsChartMode): string {
  const chartMax = analyticsChartMax(chartSeries, chartMode);
  const gridValues = [1, 0.5, 0]
    .map((gridStep) => `<span style="top:${analyticsChartTopPercent(chartMax * gridStep, chartMax)}%">${Math.round(chartMax * gridStep)}</span>`)
    .join('');
  const axisLabels = chartSeries.points
    .map((chartSample, sampleIndex) => analyticsChartAxisLabelMarkup(chartSample, sampleIndex, chartSeries.points.length))
    .join('');
  return `<div class="u-analytics-chart-grid-values" aria-hidden="true">${gridValues}</div>
    <div class="u-analytics-chart-axis-labels" aria-hidden="true">${axisLabels}</div>`;
}

function analyticsChartAxisLabelMarkup(chartSample: OmarAnalyticsChartSample, sampleIndex: number, totalSamples: number): string {
  const leftPercent = Math.round((analyticsChartX(sampleIndex, totalSamples) / OMAR_CHART_WIDTH) * 1000) / 10;
  const anchor = analyticsChartLabelAnchor(sampleIndex, totalSamples);
  return `<span style="left:${leftPercent}%" data-axis-anchor="${anchor}">${escapeHtml(chartSample.label)}</span>`;
}

function analyticsChartTopPercent(sampleValue: number, chartMax: number): number {
  return Math.round((analyticsChartY(sampleValue, chartMax) / OMAR_CHART_HEIGHT) * 1000) / 10;
}

function analyticsChartSeriesMarkup(
  chartSeries: OmarAnalyticsChartSeries,
  chartMode: OmarAnalyticsChartMode,
  chartMax: number,
): string {
  if (chartMode === 'sentiment') return analyticsSentimentSeriesMarkup(chartSeries, chartMax);
  // Regra por DADO (não por fonte): série viva não tem reach (F3.2) e vem toda
  // zerada — a polyline some em vez de desenhar uma linha falsa no eixo.
  const hasReachData = chartSeries.points.some((chartSample) => chartSample.reach > 0);
  const reachPolyline = hasReachData ? analyticsPolylineMarkup(chartSeries, 'reach', chartMax) : '';
  return `${analyticsAreaPathMarkup(chartSeries, 'mentions', chartMax)}${analyticsPolylineMarkup(chartSeries, 'mentions', chartMax)}${reachPolyline}`;
}

function analyticsSentimentSeriesMarkup(chartSeries: OmarAnalyticsChartSeries, chartMax: number): string {
  return `${analyticsPolylineMarkup(chartSeries, 'negative', chartMax)}${analyticsPolylineMarkup(chartSeries, 'positive', chartMax)}${analyticsPolylineMarkup(chartSeries, 'neutral', chartMax)}`;
}

function analyticsChartSampleLayerMarkup(chartSeries: OmarAnalyticsChartSeries, chartMode: OmarAnalyticsChartMode): string {
  return `<div class="u-analytics-chart-samples" aria-label="Amostras do grafico">${chartSeries.points.map((chartSample, sampleIndex) => analyticsChartSampleButtonMarkup(chartSeries, chartMode, sampleIndex)).join('')}</div>`;
}

function analyticsChartSampleButtonMarkup(
  chartSeries: OmarAnalyticsChartSeries,
  chartMode: OmarAnalyticsChartMode,
  sampleIndex: number,
): string {
  const chartSample = analyticsChartSampleFor(chartSeries, sampleIndex);
  const position = analyticsChartButtonPosition(chartSeries, chartMode, sampleIndex);
  return `<button type="button" class="${analyticsChartSampleClass(chartSeries, sampleIndex)}" style="left:${position.left}%;top:${position.top}%" data-analytics-chart-sample="${sampleIndex}" aria-label="${escapeHtml(chartSample.fullLabel)}">${escapeHtml(chartSample.label)}</button>`;
}

function analyticsChartSampleClass(chartSeries: OmarAnalyticsChartSeries, sampleIndex: number): string {
  const activeClass = sampleIndex === chartSeries.points.length - 1 ? ' is-active' : '';
  return `u-analytics-chart-sample${activeClass}`;
}

function analyticsChartButtonPosition(
  chartSeries: OmarAnalyticsChartSeries,
  chartMode: OmarAnalyticsChartMode,
  sampleIndex: number,
): { readonly left: number; readonly top: number } {
  const chartMax = analyticsChartMax(chartSeries, chartMode);
  const xValue = analyticsChartX(sampleIndex, chartSeries.points.length);
  const yValue = analyticsChartY(analyticsChartSampleValue(analyticsChartSampleFor(chartSeries, sampleIndex), chartMode), chartMax);
  return { left: Math.round((xValue / OMAR_CHART_WIDTH) * 1000) / 10, top: Math.round((yValue / OMAR_CHART_HEIGHT) * 1000) / 10 };
}

function analyticsChartGridMarkup(chartMax: number): string {
  return `<g class="u-analytics-chart-grid">${[0, 0.5, 1].map((gridStep) => analyticsGridLineMarkup(chartMax, gridStep)).join('')}</g>`;
}

function analyticsGridLineMarkup(chartMax: number, gridStep: number): string {
  const yValue = analyticsChartY(chartMax * gridStep, chartMax);
  return `<line x1="${OMAR_CHART_LEFT}" y1="${yValue}" x2="${OMAR_CHART_WIDTH - OMAR_CHART_RIGHT}" y2="${yValue}"/>`;
}

function analyticsPolylineMarkup(chartSeries: OmarAnalyticsChartSeries, valueKey: keyof OmarAnalyticsChartSample, chartMax: number): string {
  const polylinePoints = analyticsPolylinePoints(chartSeries, valueKey, chartMax);
  return `<polyline class="u-analytics-line ${String(valueKey)}" points="${polylinePoints}" vector-effect="non-scaling-stroke"/>`;
}

function analyticsAreaPathMarkup(chartSeries: OmarAnalyticsChartSeries, valueKey: keyof OmarAnalyticsChartSample, chartMax: number): string {
  const pathPoints = analyticsPolylinePoints(chartSeries, valueKey, chartMax);
  return `<polygon class="u-analytics-area ${String(valueKey)}" points="${OMAR_CHART_LEFT},${OMAR_CHART_BOTTOM} ${pathPoints} ${OMAR_CHART_WIDTH - OMAR_CHART_RIGHT},${OMAR_CHART_BOTTOM}"/>`;
}

function analyticsPolylinePoints(chartSeries: OmarAnalyticsChartSeries, valueKey: keyof OmarAnalyticsChartSample, chartMax: number): string {
  return chartSeries.points.map((chartSample, sampleIndex) => `${analyticsChartX(sampleIndex, chartSeries.points.length)},${analyticsChartY(Number(chartSample[valueKey]), chartMax)}`).join(' ');
}

function analyticsChartX(sampleIndex: number, totalSamples: number): number {
  const drawableWidth = OMAR_CHART_WIDTH - OMAR_CHART_LEFT - OMAR_CHART_RIGHT;
  if (totalSamples <= 1) return OMAR_CHART_LEFT;
  return Math.round(OMAR_CHART_LEFT + (sampleIndex * drawableWidth) / (totalSamples - 1));
}

function analyticsChartY(sampleValue: number, chartMax: number): number {
  const drawableHeight = OMAR_CHART_BOTTOM - OMAR_CHART_TOP;
  if (chartMax <= 0) return OMAR_CHART_BOTTOM;
  return Math.round(OMAR_CHART_BOTTOM - (sampleValue / chartMax) * drawableHeight);
}

function analyticsChartMax(chartSeries: OmarAnalyticsChartSeries, chartMode: OmarAnalyticsChartMode): number {
  const values = chartSeries.points.flatMap((chartSample) => analyticsChartValues(chartSample, chartMode));
  return Math.max(...values, 10);
}

function analyticsChartValues(chartSample: OmarAnalyticsChartSample, chartMode: OmarAnalyticsChartMode): readonly number[] {
  if (chartMode === 'sentiment') return [chartSample.negative, chartSample.neutral, chartSample.positive];
  return [chartSample.mentions, chartSample.reach];
}

function analyticsChartSampleValue(chartSample: OmarAnalyticsChartSample, chartMode: OmarAnalyticsChartMode): number {
  return chartMode === 'sentiment' ? chartSample.negative : chartSample.mentions;
}

function analyticsChartLabelAnchor(sampleIndex: number, totalSamples: number): 'start' | 'middle' | 'end' {
  if (sampleIndex === 0) return 'start';
  if (sampleIndex === totalSamples - 1) return 'end';
  return 'middle';
}

function analyticsReachMetricLine(chartSample: OmarAnalyticsChartSample): string {
  return `<div><dt>Mencoes</dt><dd>${chartSample.mentions}</dd></div><div><dt>Alcance</dt><dd>${escapeHtml(chartSample.reachLabel)}</dd></div>`;
}

function analyticsSentimentMetricLine(chartSample: OmarAnalyticsChartSample): string {
  return `<div><dt>Critico</dt><dd>${chartSample.negative}</dd></div><div><dt>Positivo</dt><dd>${chartSample.positive}</dd></div>`;
}

function analyticsChartAriaLabel(chartMode: OmarAnalyticsChartMode): string {
  return chartMode === 'sentiment' ? 'Serie de sentimento com amostras' : 'Serie de mencoes e alcance com amostras';
}
